import React, { useState, useEffect } from 'react';
import siteData from '../data/siteData.json';
import useAdminMode from '../hooks/useAdminMode';
import EditModal, { SectionEditBtn } from '../components/EditModal';
import adminApi from '../api/adminClient';
import api from '../api/client';
import './About.css';

const tabs = [
  { id: 'ceo', label: 'CEO 인사말' },
  { id: 'vision', label: '경영이념 및 비전' },
  { id: 'history', label: '조직도 및 연혁' },
  { id: 'location', label: '오시는 길' },
];

export default function About() {
  const [activeTab, setActiveTab] = useState('ceo');
  const { isAdmin } = useAdminMode();
  const [ceoMessage, setCeoMessage] = useState(siteData.ceoMessage);
  const [vision, setVision] = useState(siteData.vision);
  const [company, setCompany] = useState(siteData.company);
  const [modal, setModal] = useState(null); // { key, title, value }
  const { history, departments } = siteData;

  useEffect(() => {
    api.get('/site-content/ceo_message').then(setCeoMessage).catch(() => {});
    api.get('/site-content/vision').then(setVision).catch(() => {});
    api.get('/company').then(setCompany).catch(() => {});
  }, []);

  async function handleSave(key, value, setter) {
    try {
      await adminApi.put(`/site-content/${key}`, value);
      setter(value);
      setModal(null);
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <main>
      <div className="page-header">
        <div className="container">
          <h1>회사소개</h1>
          <p>주식회사 허브의 이야기와 가치를 소개합니다</p>
          <div className="breadcrumb">
            <span>HOME</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            <span className="current">회사소개</span>
          </div>
        </div>
      </div>

      <div className="about-tabs">
        <div className="container">
          <div className="tab-list">
            {tabs.map(t => (
              <button
                key={t.id}
                className={`tab-btn ${activeTab === t.id ? 'tab-btn--active' : ''}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {activeTab === 'ceo' && (
            <CeoSection
              data={ceoMessage}
              isAdmin={isAdmin}
              onEdit={() => setModal({ key: 'ceo_message', title: 'CEO 인사말 편집', value: ceoMessage, setter: setCeoMessage })}
            />
          )}
          {activeTab === 'vision' && (
            <VisionSection
              data={vision}
              isAdmin={isAdmin}
              onEdit={() => setModal({ key: 'vision', title: '경영이념 및 비전 편집', value: vision, setter: setVision })}
            />
          )}
          {activeTab === 'history' && <HistorySection history={history} departments={departments} />}
          {activeTab === 'location' && <LocationSection company={company} />}
        </div>
      </section>

      {modal && (
        <EditModal
          title={modal.title}
          value={modal.value}
          onSave={(val) => handleSave(modal.key, val, modal.setter)}
          onClose={() => setModal(null)}
        />
      )}
    </main>
  );
}

/* ── CEO ── */
function CeoSection({ data, isAdmin, onEdit }) {
  return (
    <div className="ceo-wrapper">
      <div className="ceo-message">
        <h2>
          CEO 인사말
          {isAdmin && <SectionEditBtn onClick={onEdit} />}
        </h2>
        <div className="divider" style={{ margin: '1rem 0 1.5rem', marginLeft: 0 }} />
        <div className="ceo-message__text">
          {data.message.split('\n\n').map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
        <div className="ceo-signature">{data.signature}</div>
      </div>
    </div>
  );
}

/* ── Vision ── */
function VisionSection({ data, isAdmin, onEdit }) {
  return (
    <div>
      <div className="section-header" style={{ textAlign: 'left' }}>
        <span className="eyebrow">Vision & Mission</span>
        <h2>
          경영이념 및 비전
          {isAdmin && <SectionEditBtn onClick={onEdit} />}
        </h2>
        <div className="divider" style={{ margin: '1rem 0 0', marginLeft: 0 }} />
      </div>
      <div className="vision-blocks">
        <div className="vision-block vision-block--mission">
          <span className="vision-block__label">MISSION</span>
          <p>{data.mission}</p>
        </div>
        <div className="vision-block vision-block--vision">
          <span className="vision-block__label">VISION</span>
          <p>{data.vision}</p>
        </div>
      </div>
      <h3 className="values-title">핵심 가치 (Core Values)</h3>
      <div className="values-grid">
        {data.values.map((v, i) => (
          <div key={v.id} className="value-card">
            <div className="value-card__number">0{i + 1}</div>
            <h4>{v.title}</h4>
            <p>{v.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── History ── */
function HistorySection({ history, departments }) {
  return (
    <div>
      <div className="section-header" style={{ textAlign: 'left' }}>
        <span className="eyebrow">Organization & History</span>
        <h2>조직도 및 연혁</h2>
        <div className="divider" style={{ margin: '1rem 0 0', marginLeft: 0 }} />
      </div>

      <h3 className="sub-section-title">조직도</h3>
      <div className="org-chart">
        {departments[0] && <OrgNode node={departments[0]} />}
      </div>

    </div>
  );
}

function OrgNode({ node, depth = 0 }) {
  return (
    <div className={`org-node org-node--depth-${depth}`}>
      <div className="org-node__box">{node.name}</div>
      {node.children && (
        <div className="org-node__children">
          {node.children.map((child, i) => (
            <OrgNode key={i} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Location ── */
function LocationSection({ company }) {
  return (
    <div>
      <div className="section-header" style={{ textAlign: 'left' }}>
        <span className="eyebrow">Location</span>
        <h2>오시는 길</h2>
        <div className="divider" style={{ margin: '1rem 0 0', marginLeft: 0 }} />
      </div>
      <div className="location-wrapper">
        <div className="location-info">
          <h3>주식회사 허브 본사</h3>
          <ul className="location-info__list">
            <li>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <div><strong>주소</strong><span>{company.address}</span></div>
            </li>
            <li>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 01.06 2.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              <div><strong>전화</strong><span><a href={`tel:${company.phone}`}>{company.phone}</a></span></div>
            </li>
            <li>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 01.06 2.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              <div><strong>팩스</strong><span>{company.fax}</span></div>
            </li>
            <li>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              <div><strong>이메일</strong><span><a href={`mailto:${company.email}`}>{company.email}</a></span></div>
            </li>
            <li>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <div><strong>업무시간</strong><span>{company.businessHours || company.business_hours}</span></div>
            </li>
          </ul>
          <div className="location-transport">
            <h4>교통편 안내</h4>
            {(company.subwayInfo || company.subway_info) && (
              <div className="transport-item">
                <span className="transport-badge transport-badge--subway">지하철</span>
                <span>{company.subwayInfo || company.subway_info}</span>
              </div>
            )}
            {(company.busInfo || company.bus_info) && (
              <div className="transport-item">
                <span className="transport-badge transport-badge--bus">버스</span>
                <span>{company.busInfo || company.bus_info}</span>
              </div>
            )}
            {(company.carInfo || company.car_info) && (
              <div className="transport-item">
                <span className="transport-badge transport-badge--car">자가용</span>
                <span>{company.carInfo || company.car_info}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
